import React, { ReactElement } from 'react';
import PropTypes from 'prop-types';
import { ShortText } from '../Typography';
import { InternalIconProps } from '../Icon/GenericIcon';
import { TagProps } from './types';
import { StyledTag, StyledIconWrapper } from './Tag.styles';

// Helper method to please the TS parser: without it, TS doesn't understand that `displayName` is available.
const extractDisplayName = (type: React.FC) => type.displayName || type.name || 'unknown';

export const Tag = ({
  children,
  color = 'default',
  icon,
  alignIcon = 'start',
  title,
}: TagProps) => {
  let iconContent: ReactElement<InternalIconProps> | undefined = icon;
  let Icon;

  // TODO: Create util and replace when it applies
  if (iconContent?.props.color !== undefined) {
    console.warn(
      'Please remove `color` property from <Icon /> in <Tag /> component. This property will be ignored, as we want to make sure that icon and text color in the Tag always match.'
    );
  }

  if (iconContent) {
    // We want `Tag` to be used only with bold icons for a consistent UI.
    // Log an error when the given icon is not a bold one.
    if (typeof iconContent.type === 'function') {
      const displayName = extractDisplayName(iconContent.type as React.FC);

      if (!displayName?.endsWith('Bold')) {
        console.error(
          `You are using the icon <${displayName} /> within <Tag />. Please use a Bold icon instead.`
        );
      }
    }

    // Color property in icon will be ignored, as we want to make sure that icon and text color in the button always match.
    iconContent = React.cloneElement(iconContent, { color: 'currentColor', size: 'xsmall' });
    Icon = <StyledIconWrapper alignIcon={alignIcon}>{iconContent}</StyledIconWrapper>;
  }

  return (
    <StyledTag color={color} role="status" aria-label={title} aria-live="polite">
      {Icon && alignIcon === 'start' && Icon}
      <ShortText color="inherit" size="small" weight="strong" as="span">
        {children}
      </ShortText>
      {Icon && alignIcon === 'end' && Icon}
    </StyledTag>
  );
};

// Some clients use the old color values. So we keep and map them in the component to the new values.
// TODO: remove once all clients finished migrating to the new values or we created a codemod.
const tagColorLegacyValues = [
  'information',
  'important',
  'locked',
  'warning',
  'available',
  'unavailable',
  'premium',
];

Tag.propTypes = {
  children: PropTypes.node,
  icon: PropTypes.node,
  alignIcon: PropTypes.oneOf(['start', 'end'] as const),
  color: PropTypes.oneOf([
    ...tagColorLegacyValues,
    'default',
    'default-lightened',
    'default-inverse',
    'translucent',
    'translucent-dark',
    'success',
    'warning',
    'failure',
  ] as const),
  title: PropTypes.string,
};
